import SaveIcon from "@mui/icons-material/Save";
import { LoadingButton } from "@mui/lab";
import { Box, Grid2, TextField, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { IRTODetails } from ".";
import { AGDialog } from "../../components/AGDialog";
import { useAppSelector } from "../../redux";
import { useCreateJurisdictionMutation, useEditJurisdictionMutation } from "../../redux/reducers/jurisdictions.reducer";
import { JURISDICTION_TYPE_E } from "../../types";
import { VALIDATION_REGEX } from "../../utils/constants";

interface IRTODetailsDialogProps {
	open: boolean;
	rtoDetails: IRTODetails | null;
	jurisdictionId: number;
	onClose: () => void;
}

interface IRTODetailsForm {
	name: string;
	rto_code: string;
	description: string;
}

const RTODetailsDialog: FC<IRTODetailsDialogProps> = (props) => {
	const { open, onClose, rtoDetails, jurisdictionId } = props;

	const authState = useAppSelector((state) => state.auth);

	const [apiError, setApiError] = useState<string>("");

	// APIS
	// CREATE JURISDICTION
	const [createJurisdiction, { isLoading: createJurisdictionLoading }] = useCreateJurisdictionMutation();

	// EDIT JURISDICTION
	const [editJurisdiction, { isLoading: editJurisdictionLoading }] = useEditJurisdictionMutation();

	const readOnlyInput = useMemo<boolean>(() => {
		if (authState.user.permissions && authState.user.permissions.rto_metadata?.edit) return false;
		return true;
	}, [authState.user.permissions]);

	const {
		reset,
		register,
		handleSubmit,
		formState: { errors, isDirty: inputChanged }
	} = useForm<IRTODetailsForm>();

	function handleSubmitForm(formData: IRTODetailsForm) {
		if (rtoDetails) {
			editJurisdiction({
				id: rtoDetails.id,
				data: {
					description: formData.description,
					jurisdiction_type: JURISDICTION_TYPE_E.RTOS,
					name: formData.name,
					parent: jurisdictionId,
					rto_code: formData.rto_code?.toUpperCase() ?? ""
				}
			})
				.unwrap()
				.then(() => {
					setApiError("");
					onClose();
				})
				.catch((error) => {
					setApiError("data" in error && error.data ? String(error.data) : "Failed to save RTO details");
				});
		} else {
			createJurisdiction({
				name: formData.name,
				jurisdiction_type: JURISDICTION_TYPE_E.RTOS,
				parent: jurisdictionId,
				description: formData.description,
				rto_code: formData.rto_code?.toUpperCase() ?? ""
			})
				.unwrap()
				.then(() => {
					setApiError("");
					onClose();
				})
				.catch((error) => {
					setApiError("data" in error && error.data ? String(error.data) : "Failed to save RTO details");
				});
		}
	}

	useEffect(() => {
		if (open) {
			reset({
				name: rtoDetails?.name ?? "",
				rto_code: rtoDetails?.rto_code ?? "",
				description: rtoDetails?.description ?? ""
			});
			setApiError("");
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, rtoDetails]);

	return (
		<AGDialog open={open} onClose={onClose} title="RTO Details" color="primary" maxWidth="xs">
			<Box component="form" noValidate onSubmit={handleSubmit(handleSubmitForm)}>
				<Grid2 container spacing={2}>
					<Grid2 size={{ xs: 12 }}>
						<TextField
							required
							fullWidth
							size="small"
							label="RTO Code"
							autoComplete="off"
							error={!!errors.rto_code}
							helperText={errors.rto_code?.message?.toString() || ""}
							slotProps={{ input: { readOnly: readOnlyInput, classes: { root: "vehicle-number-input-root" } } }}
							{...register("rto_code", {
								required: "RTO code is required",
								pattern: { value: VALIDATION_REGEX.RTO_CODE, message: "Invalid RTO code" }
							})}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<TextField
							required
							fullWidth
							size="small"
							label="RTO Name"
							autoComplete="off"
							error={!!errors.name}
							helperText={errors.name?.message?.toString() || ""}
							slotProps={{ input: { readOnly: readOnlyInput } }}
							{...register("name", {
								required: "RTO name is required",
								setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
							})}
						/>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<TextField
							fullWidth
							multiline
							minRows={3}
							size="small"
							label="Description"
							autoComplete="off"
							error={!!errors.description}
							helperText={errors.description?.message?.toString() || ""}
							slotProps={{ input: { readOnly: readOnlyInput } }}
							{...register("description", {
								setValueAs: (value) => (typeof value === "string" ? value.trim() : value)
							})}
						/>
					</Grid2>

					{apiError ? (
						<Grid2 size={{ xs: 12 }}>
							<Typography variant="body2" color="error" textAlign="center">
								{apiError}
							</Typography>
						</Grid2>
					) : null}

					{!readOnlyInput ? (
						<Grid2 size={{ xs: 12 }}>
							<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
								<LoadingButton
									disableElevation
									variant="contained"
									color="primary"
									type="submit"
									startIcon={<SaveIcon />}
									loading={createJurisdictionLoading || editJurisdictionLoading}
									loadingPosition="start"
									disabled={!inputChanged}
								>
									Save
								</LoadingButton>
							</Box>
						</Grid2>
					) : null}
				</Grid2>
			</Box>
		</AGDialog>
	);
};

export default RTODetailsDialog;
